import React, { useState } from 'react';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import LearningIllustration from './LearningIllustration';

const BOARDS = ['CBSE', 'ICSE', 'State Board'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getErrorMessage(code) {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password. Please try again.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AuthPage() {
  const { setUserProfile } = useAuth();

  // mode: 'login' | 'choose-role' | 'teacher-signup' | 'student-signup'
  const [mode, setMode] = useState('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── Login state ──
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  // ── Teacher signup state ──
  const [teacherForm, setTeacherForm] = useState({
    name: '',
    email: '',
    school: '',
    city: '',
    board: 'CBSE',
    password: '',
    confirm: '',
  });

  // ── Student signup state ──
  const [studentForm, setStudentForm] = useState({
    name: '',
    email: '',
    password: '',
    confirm: '',
  });

  // ── Navigation helpers ──
  const goTo = (nextMode) => {
    setMode(nextMode);
    setError('');
  };

  // ── Login ──
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
      // onAuthStateChanged in AuthContext handles the rest
    } catch (err) {
      setError(getErrorMessage(err.code));
    }
    setLoading(false);
  };

  // ── Teacher Signup ──
  const handleTeacherSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (!teacherForm.name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (teacherForm.password !== teacherForm.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (teacherForm.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        teacherForm.email,
        teacherForm.password
      );

      const profileData = {
        name: teacherForm.name.trim(),
        email: teacherForm.email,
        role: 'teacher',
        school: teacherForm.school.trim(),
        city: teacherForm.city.trim(),
        board: teacherForm.board,
      };

      // Write user doc
      await setDoc(doc(db, 'users', user.uid), {
        ...profileData,
        createdAt: serverTimestamp(),
      });

      // Write school doc — used by students to search and find this teacher
      await setDoc(doc(db, 'schools', user.uid), {
        name: teacherForm.school.trim(),
        nameLower: teacherForm.school.trim().toLowerCase(),
        city: teacherForm.city.trim(),
        board: teacherForm.board,
        teacherId: user.uid,
        teacherName: teacherForm.name.trim(),
      });

      // Update context immediately so routing kicks in without waiting for
      // another onAuthStateChanged cycle
      setUserProfile(profileData);
    } catch (err) {
      setError(getErrorMessage(err.code));
    }
    setLoading(false);
  };

  // ── Student Signup ──
  const handleStudentSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (!studentForm.name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (studentForm.password !== studentForm.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (studentForm.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        studentForm.email,
        studentForm.password
      );

      const profileData = {
        name: studentForm.name.trim(),
        email: studentForm.email,
        role: 'student',
      };

      await setDoc(doc(db, 'users', user.uid), {
        ...profileData,
        createdAt: serverTimestamp(),
      });

      // Update context immediately
      setUserProfile(profileData);
    } catch (err) {
      setError(getErrorMessage(err.code));
    }
    setLoading(false);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="auth-wrapper">
      <div className="auth-card">

        {/* ── Illustration — login screen only ── */}
        {mode === 'login' && (
          <div className="auth-illustration">
            <LearningIllustration />
          </div>
        )}

        {/* Logo — always visible */}
        <div className="auth-logo">
          <span className="logo-icon">🪺</span>
          <h1 className="logo-text">LittleNest</h1>
          <p className="logo-tagline">Where learning begins</p>
        </div>

        {/* ── LOGIN ── */}
        {mode === 'login' && (
          <>
            <h2 className="auth-title">Welcome back</h2>
            {error && <div className="error-box">{error}</div>}
            <form onSubmit={handleLogin}>
              <div className="field">
                <label>Email address</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="field">
                <label>Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
            <p className="auth-switch">
              New to LittleNest?{' '}
              <button className="link-btn" onClick={() => goTo('choose-role')}>
                Create an account
              </button>
            </p>
          </>
        )}

        {/* ── CHOOSE ROLE ── */}
        {mode === 'choose-role' && (
          <>
            <h2 className="auth-title">Join LittleNest</h2>
            <p className="auth-subtitle">Are you a teacher or a student?</p>
            <div className="role-cards">
              <button className="role-card" onClick={() => goTo('teacher-signup')}>
                <span className="role-icon">📚</span>
                <span className="role-label">I'm a Teacher</span>
                <span className="role-desc">Set up your classroom and manage your students</span>
              </button>
              <button className="role-card" onClick={() => goTo('student-signup')}>
                <span className="role-icon">🎒</span>
                <span className="role-label">I'm a Student</span>
                <span className="role-desc">Join your teacher's class and start learning</span>
              </button>
            </div>
            <p className="auth-switch">
              Already have an account?{' '}
              <button className="link-btn" onClick={() => goTo('login')}>
                Sign in
              </button>
            </p>
          </>
        )}

        {/* ── TEACHER SIGNUP ── */}
        {mode === 'teacher-signup' && (
          <>
            <button className="back-btn" onClick={() => goTo('choose-role')}>
              ← Back
            </button>
            <h2 className="auth-title">Teacher Sign Up</h2>
            {error && <div className="error-box">{error}</div>}
            <form onSubmit={handleTeacherSignup}>
              <div className="field">
                <label>Full Name</label>
                <input
                  type="text"
                  value={teacherForm.name}
                  onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })}
                  placeholder="Your full name"
                  required
                  autoComplete="name"
                />
              </div>
              <div className="field">
                <label>Email address</label>
                <input
                  type="email"
                  value={teacherForm.email}
                  onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value })}
                  placeholder="you@school.edu"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="field">
                <label>School Name</label>
                <input
                  type="text"
                  value={teacherForm.school}
                  onChange={(e) => setTeacherForm({ ...teacherForm, school: e.target.value })}
                  placeholder="e.g. St. Mary's Higher Secondary School"
                  required
                />
              </div>
              <div className="field-row">
                <div className="field">
                  <label>City</label>
                  <input
                    type="text"
                    value={teacherForm.city}
                    onChange={(e) => setTeacherForm({ ...teacherForm, city: e.target.value })}
                    placeholder="Chennai"
                    required
                  />
                </div>
                <div className="field">
                  <label>Board</label>
                  <select
                    value={teacherForm.board}
                    onChange={(e) => setTeacherForm({ ...teacherForm, board: e.target.value })}
                  >
                    {BOARDS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field">
                <label>Password</label>
                <input
                  type="password"
                  value={teacherForm.password}
                  onChange={(e) => setTeacherForm({ ...teacherForm, password: e.target.value })}
                  placeholder="At least 6 characters"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="field">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={teacherForm.confirm}
                  onChange={(e) => setTeacherForm({ ...teacherForm, confirm: e.target.value })}
                  placeholder="Repeat your password"
                  required
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Creating account…' : 'Create Teacher Account'}
              </button>
            </form>
          </>
        )}

        {/* ── STUDENT SIGNUP ── */}
        {mode === 'student-signup' && (
          <>
            <button className="back-btn" onClick={() => goTo('choose-role')}>
              ← Back
            </button>
            <h2 className="auth-title">Student Sign Up</h2>
            <p className="auth-subtitle">
              After signing up you'll search for your school and send a join request to your teacher.
            </p>
            {error && <div className="error-box">{error}</div>}
            <form onSubmit={handleStudentSignup}>
              <div className="field">
                <label>Full Name</label>
                <input
                  type="text"
                  value={studentForm.name}
                  onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                  placeholder="Your full name"
                  required
                  autoComplete="name"
                />
              </div>
              <div className="field">
                <label>Email address</label>
                <input
                  type="email"
                  value={studentForm.email}
                  onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="field">
                <label>Password</label>
                <input
                  type="password"
                  value={studentForm.password}
                  onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
                  placeholder="At least 6 characters"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="field">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={studentForm.confirm}
                  onChange={(e) => setStudentForm({ ...studentForm, confirm: e.target.value })}
                  placeholder="Repeat your password"
                  required
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Creating account…' : 'Create Student Account'}
              </button>
            </form>
          </>
        )}

      </div>
    </div>
  );
}
